import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export function useSectionScroll() {
  const location = useLocation();

  useEffect(() => {
    // Valid sections on each page
    const validSections = {
      '/': ['home', 'features', 'video', 'pricing', 'testimonials', 'faq', 'try'],
      '/coverage': ['croatia', 'austria', 'eu'],
      '/security': ['encryption', 'compliance', 'privacy'],
    };

    const scrollToSection = (attemptCount = 0) => {
      // Parse URL parameters for section
      const params = new URLSearchParams(location.search);
      const section = params.get('section');

      // Also support hash-based anchors (e.g., #features)
      const hashSection = location.hash?.replace('#', '');

      const targetSection = section || hashSection;

      if (targetSection) {
        // Check if section is valid for current page
        const currentPageSections = validSections[location.pathname] || [];
        const isValidSection = currentPageSections.includes(targetSection);

        const element = document.getElementById(targetSection);
        if (element) {
          // Use requestAnimationFrame to wait for next paint cycle
          requestAnimationFrame(() => {
            // Double RAF to ensure layout is complete
            requestAnimationFrame(() => {
              // Calculate position with offset for navbar
              const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
              // Special handling for 'try' section which needs more offset
              const navbarHeight = targetSection === 'try' ? 150 : 100;
              const offsetPosition = elementPosition - navbarHeight;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            });
          });
        } else if (attemptCount < 10 && isValidSection) {
          // Only retry if section should exist on this page
          requestAnimationFrame(() => scrollToSection(attemptCount + 1));
        }
      }
    };

    // Wait for document ready state and any pending renders
    if (document.readyState === 'complete') {
      scrollToSection();
    } else {
      const handleLoad = () => {
        scrollToSection();
        window.removeEventListener('load', handleLoad);
      };
      window.addEventListener('load', handleLoad);

      // Also try after DOMContentLoaded
      if (document.readyState === 'interactive' || document.readyState === 'complete') {
        scrollToSection();
      }
    }

    return () => {
      // Cleanup not needed for timeouts that complete
    };
  }, [location.search, location.hash, location.key, location.pathname]);
}

export function useScrollToTop() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
}
